/** Curriculum structure rules (Parcours → Semestre → UE → ECUE). Pure functions, shared by API and UI. */

export const UNIT_NATURES = ['F', 'T', 'O'] as const;
export type UnitNature = (typeof UNIT_NATURES)[number];

export const REGIMES = ['MIXTE', 'CC', 'EXAM'] as const;
export type Regime = (typeof REGIMES)[number];

export interface CourseLike {
  credits: number;
  coefficient: number;
}
export interface UnitLike {
  nature: UnitNature;
  courses: readonly CourseLike[];
}
export interface SemesterLike {
  id: string;
  index: number;
  targetCredits: number;
  units: readonly UnitLike[];
}

/**
 * UE credits = sum of its ECUE credits. An optional UE ("au choix") is worth the credits of
 * one choice: the maximum of its ECUE (design: ScrCurriculum `ueCr`).
 */
export function unitCredits(unit: UnitLike): number {
  if (unit.courses.length === 0) return 0;
  return unit.nature === 'O'
    ? Math.max(...unit.courses.map((c) => c.credits))
    : unit.courses.reduce((a, c) => a + c.credits, 0);
}

/** UE coefficient = sum of its ECUE coefficients (rounded to 2 decimals to avoid float noise). */
export function unitCoefficient(unit: UnitLike): number {
  return Math.round(unit.courses.reduce((a, c) => a + c.coefficient, 0) * 100) / 100;
}

export function semesterCredits(units: readonly UnitLike[]): number {
  return units.reduce((a, u) => a + unitCredits(u), 0);
}

export interface StructureIssue {
  semesterId: string;
  index: number;
  credits: number;
  target: number;
}

/** A curriculum can be published only when every semester totals exactly its target (30 ECTS). */
export function validateStructure(semesters: readonly SemesterLike[]): {
  ok: boolean;
  issues: StructureIssue[];
} {
  const issues = semesters
    .map((s) => ({
      semesterId: s.id,
      index: s.index,
      credits: semesterCredits(s.units),
      target: s.targetCredits,
    }))
    .filter((s) => s.credits !== s.target);
  return { ok: issues.length === 0 && semesters.length > 0, issues };
}

/** Semester index (1-based) → year of study (S1,S2 → 1; S3,S4 → 2 …). */
export const yearOfSemester = (index: number) => Math.ceil(index / 2);
